import { useState } from "react"
import { PhoneOutgoing, X as XIcon } from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { parseServerDate } from "@/lib/utils"

/**
 * Rückruf-Versuche an einem Anruf dokumentieren (#151).
 *
 * Marlene: nach einem erfolglosen Rückruf gibt es keinen Ort, das festzuhalten
 * — „am Ende der Schicht dokumentieren, am besten Freifeld mit Status". Paul
 * (04.10.2026): „Die Liste passt, kein Logbuch."
 *
 * 🔑 Ein Eintrag je Versuch, mehrere je Anruf. In der Liste steht der neueste
 * als Badge, damit die nächste Schicht sofort sieht, was los ist.
 * 🔴 Bleibt im Portal — nichts davon geht ins HERO-Logbuch.
 */

/** Die Schlüssel kommen aus `database/anruf_rueckrufe.py` (STATUS); der
 *  Wächter `tests/test_anruf_rueckrufe.py` hält beide Listen gleich. */
export const RUECKRUF_STATUS = [
  { wert: "nicht_erreicht", label: "nicht erreicht", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  { wert: "erreicht_erledigt", label: "erreicht, erledigt", cls: "bg-success/10 text-success border-success/30" },
  { wert: "erneut_zurueckrufen", label: "erneut zurückrufen", cls: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
  { wert: "mailbox", label: "Mailbox besprochen", cls: "bg-sky-500/10 text-sky-700 border-sky-500/30" },
]

export type RueckrufEintrag = {
  id?: number
  call_id?: string
  status: string
  notiz?: string | null
  erfasst_von?: string | null
  created_at?: string | null
  /** nur in der Liste: wie viele Versuche es insgesamt gab */
  anzahl?: number
}

/** #228 (Robin, 02.11.2026): nur etwas festhalten, ohne zurückgerufen zu
 *  haben. Kein Rückruf-Ergebnis — deshalb nicht in RUECKRUF_STATUS; der Server
 *  zählt es weder als Versuch noch als Stand der Liste. */
export const NUR_NOTIZ = { wert: "notiz", label: "Notiz", cls: "bg-secondary text-foreground/80 border-border" }

const statusVon = (wert: string) => RUECKRUF_STATUS.find((s) => s.wert === wert) ?? (wert === NUR_NOTIZ.wert ? NUR_NOTIZ : undefined)

export function rueckrufLabel(wert: string): string {
  return statusVon(wert)?.label ?? wert
}

/** Der Rückruf ist abgeschlossen — „erreicht, erledigt". */
export function rueckrufErledigt(e?: RueckrufEintrag | null): boolean {
  return e?.status === "erreicht_erledigt"
}

function wann(iso?: string | null): string {
  const d = parseServerDate(iso)
  if (!d) return ""
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
}

/** Kurzform für Listen: Status, wer, wann — und die Notiz darunter.
 *
 *  #168 (Marlene, 08.10.2026): „Es muss zumindest erkenntlich sein, dass eine
 *  Rückrufnotiz vorliegt, besser noch, dass man sie auf den ersten Blick sehen
 *  kann." Bis dahin stand sie nur im Tooltip. Lange Notizen werden auf zwei
 *  Zeilen gekürzt; der volle Text steht weiter im Tooltip. */
export function RueckrufBadge({ eintrag }: { eintrag?: RueckrufEintrag | null }) {
  if (!eintrag) return null
  const s = statusVon(eintrag.status)
  const zusatz = [eintrag.erfasst_von, wann(eintrag.created_at)].filter(Boolean).join(" · ")
  const titel = [
    `Rückruf: ${rueckrufLabel(eintrag.status)}`,
    zusatz,
    eintrag.notiz || "",
    (eintrag.anzahl ?? 0) > 1 ? `${eintrag.anzahl} Versuche insgesamt` : "",
  ].filter(Boolean).join("\n")
  return (
    <span className="inline-flex flex-col gap-0.5" data-testid="rueckruf-badge" title={titel}>
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10.5px] font-semibold w-fit ${s?.cls ?? "bg-secondary"}`}>
        <PhoneOutgoing className="w-3 h-3" />
        Rückruf: {rueckrufLabel(eintrag.status)}
        {(eintrag.anzahl ?? 0) > 1 && <span className="font-normal">({eintrag.anzahl}×)</span>}
      </span>
      {zusatz && <span className="text-[10.5px] text-muted-foreground">{zusatz}</span>}
      {eintrag.notiz && (
        <span className="text-[11px] text-foreground/80 italic line-clamp-2 max-w-[260px] whitespace-pre-line"
              data-testid="rueckruf-notiz" data-pii="">
          „{eintrag.notiz}“
        </span>
      )}
    </span>
  )
}

/** Status wählen, Notiz frei, speichern. Wer und wann setzt der Server. */
export function RueckrufFormular({ callId, onGespeichert, url }: {
  callId: string
  onGespeichert?: (antwort: { eintrag: RueckrufEintrag; flag_erledigt: boolean }) => void
  /** #162: anderer Speicherort, z. B. „Telefonat vermerken" an einer Mail. */
  url?: string
}) {
  const [status, setStatus] = useState<string>("")
  const [notiz, setNotiz] = useState("")
  const [busy, setBusy] = useState(false)

  // Am Mail-Vermerk (#162) ist jeder Eintrag ein Telefonat — dort keine reine Notiz.
  const nurNotizMoeglich = !url && !status && !!notiz.trim()

  const speichern = async (nurNotiz = false) => {
    if (!status && !nurNotiz) return
    setBusy(true)
    try {
      const r = await fetch(url ?? `/admin/ki-telefon/api/call/${encodeURIComponent(callId)}/rueckruf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: nurNotiz ? NUR_NOTIZ.wert : status, notiz }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`)
      toast(nurNotiz ? "Notiz gespeichert" : d.flag_erledigt ? "Rückruf eingetragen — Rückruf-Hinweis abgehakt" : url ? "Telefonat vermerkt" : "Rückruf eingetragen", "success")
      setStatus("")
      setNotiz("")
      onGespeichert?.(d)
    } catch (e) {
      toast("Fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="rueckruf-formular">
      {/* Ohne gewähltes Ergebnis bleibt der Knopf grau — das muss man sehen,
          sonst tippt man die Notiz und wundert sich (Paul, 02.11.2026). */}
      <div className="text-[12px] font-medium text-muted-foreground">Wie ist das Telefonat ausgegangen?</div>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Rückruf-Status">
        {RUECKRUF_STATUS.map((s) => (
          <button
            key={s.wert}
            type="button"
            role="radio"
            aria-checked={status === s.wert}
            onClick={() => setStatus(s.wert)}
            className={`px-2 py-1 rounded border text-[12px] font-medium transition-colors ${
              status === s.wert ? s.cls + " ring-1 ring-current" : "bg-card hover:bg-secondary border-border"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <textarea
        value={notiz}
        onChange={(e) => setNotiz(e.target.value)}
        placeholder="Notiz (optional), z. B. „AB voll, morgen ab 9 Uhr nochmal“"
        aria-label="Notiz zum Rückruf"
        maxLength={2000}
        rows={2}
        className="w-full px-2 py-1 rounded border text-[12.5px] bg-card"
      />
      <div>
        <button
          type="button"
          onClick={() => speichern()}
          disabled={!status || busy}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-primary text-primary-foreground text-[12px] font-medium hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Speichere..." : url ? "Telefonat vermerken" : "Rückruf eintragen"}
        </button>
        {nurNotizMoeglich && (
          <button
            type="button"
            onClick={() => speichern(true)}
            disabled={busy}
            className="ml-2 inline-flex items-center gap-1 px-3 py-1 rounded-md border text-[12px] font-medium hover:bg-secondary disabled:opacity-50"
            title="Notiz ohne Rückruf-Ergebnis festhalten — zählt nicht als Rückrufversuch"
          >
            Nur Notiz speichern
          </button>
        )}
        {!status && !nurNotizMoeglich && (
          <span className="ml-2 text-[11.5px] text-muted-foreground" data-testid="rueckruf-hinweis">
            Erst oben das Ergebnis wählen{url ? "" : " — oder nur eine Notiz schreiben"}
          </span>
        )}
      </div>
    </div>
  )
}

/** Alle Versuche, neueste zuerst. */
export function RueckrufVerlauf({ eintraege }: { eintraege?: RueckrufEintrag[] | null }) {
  if (!eintraege?.length) {
    return <div className="text-[12px] text-muted-foreground">Noch kein Rückruf dokumentiert.</div>
  }
  return (
    <ul className="space-y-1.5" data-testid="rueckruf-verlauf">
      {eintraege.map((e, i) => (
        <li key={e.id ?? i} className="text-[12.5px] flex flex-col">
          <span>
            <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10.5px] font-semibold mr-1.5 ${statusVon(e.status)?.cls ?? "bg-secondary"}`}>
              {rueckrufLabel(e.status)}
            </span>
            <span className="text-muted-foreground text-[11.5px]">
              {[e.erfasst_von, wann(e.created_at)].filter(Boolean).join(" · ")}
            </span>
          </span>
          {e.notiz && <span className="whitespace-pre-wrap mt-0.5">{e.notiz}</span>}
        </li>
      ))}
    </ul>
  )
}

/** Für die Listenzeile: kleiner Knopf, der das Formular als Dialog öffnet. */
export function RueckrufEintragenKnopf({ callId, onChange, url, label = "Rückruf eintragen" }: {
  callId: string
  onChange?: () => void
  /** #162: siehe `RueckrufFormular.url` */
  url?: string
  label?: string
}) {
  const [offen, setOffen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOffen(true)}
        className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline w-fit"
      >
        <PhoneOutgoing className="w-3 h-3" /> {label}
      </button>
      {offen && (
        <div
          className="pp-overlay"
          style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.4)",
                   display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setOffen(false)}
        >
          <div
            role="dialog"
            aria-label={label}
            onClick={(e) => e.stopPropagation()}
            className="bg-card rounded-lg border shadow-lg p-4 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[14px] font-semibold">{label}</h3>
              <button type="button" onClick={() => setOffen(false)} aria-label="Schließen"
                      className="text-muted-foreground hover:text-foreground">
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <RueckrufFormular
              callId={callId}
              url={url}
              onGespeichert={() => { setOffen(false); onChange?.() }}
            />
          </div>
        </div>
      )}
    </>
  )
}
